/**
 * AuthGuard — protects routes that require authentication.
 *
 * Redirects unauthenticated users to the sign-in page.
 * Preserves the intended destination so users can be
 * redirected back after signing in. Shows a loading
 * state while auth is being restored on first load.
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/auth';
import { ROUTES } from '@/app/routes/paths';
import { Spinner } from '@/components/ui/Spinner';
import type { GuardProps } from './types';

export function AuthGuard({ children }: GuardProps): JSX.Element {
  const { state } = useAuth();
  const location = useLocation();

  if (state === 'loading') {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" role="status" aria-live="polite">
        <Spinner size="lg" />
      </div>
    );
  }

  if (state === 'unauthenticated') {
    return <Navigate to={ROUTES.LOGIN} replace state={{ from: location }} />;
  }

  return <>{children}</>;
}

AuthGuard.displayName = 'AuthGuard';
